import React from 'react';

export const GlobalStyles: React.FC = () => {
    return (
        <style>{`
            * {
                font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
                -webkit-font-smoothing: antialiased;
                -moz-osx-font-smoothing: grayscale;
                word-wrap: break-word;
                overflow-wrap: break-word;
                hyphens: none;
            }
            
            h1, h2, h3, h4, h5, h6 {
                letter-spacing: -0.02em;
                word-break: keep-all;
            }
            
            body, p, span, div, a, label, button {
                letter-spacing: -0.01em;
                word-break: keep-all;
            }
            
            @keyframes fadeInUp {
                from {
                    opacity: 0;
                    transform: translateY(30px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }

            @keyframes fadeIn {
                from {
                    opacity: 0;
                }
                to {
                    opacity: 1;
                }
            }

            @keyframes fadeInLeft {
                from {
                    opacity: 0;
                    transform: translateX(-50px);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            @keyframes fadeInRight {
                from {
                    opacity: 0;
                    transform: translateX(50px);
                }
                to {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            @keyframes laser {
                0% {
                    transform: translateX(-200px);
                }
                100% {
                    transform: translateX(calc(100vw + 200px));
                }
            }

            .fade-in-up {
                animation: fadeInUp 0.6s ease-out forwards;
            }

            .fade-in-left {
                animation: fadeInLeft 0.6s ease-out forwards;
            }

            .fade-in-right {
                animation: fadeInRight 0.6s ease-out forwards;
            }

            .fade-in {
                animation: fadeIn 0.6s ease-out forwards;
            }

            .laser-beam {
                position: absolute;
                top: 0;
                left: 0;
                height: 100%;
                width: 200px;
                background: rgba(255, 255, 255);
                filter: blur(0.3px);
                animation: laser 3s linear infinite;
                pointer-events: none;
            }

            .title-letter {
                display: inline-block;
                font-weight: inherit;
            }

            .letter-scrambled {
                opacity: 0;
                transform: translate(var(--random-x, 0), var(--random-y, 0));
            }

            @keyframes letterAlign {
                0% {
                    opacity: 0;
                    transform: translate(var(--random-x, 0), var(--random-y, 0));
                }
                50% {
                    opacity: 0;
                    transform: translate(var(--random-x, 0), var(--random-y, 0));
                }
                70% {
                    opacity: 1;
                    transform: translate(0, 0);
                }
            }

            .animate-letter-align {
                animation: letterAlign 2.1s ease-out forwards;
            }

            /* Notebook grid backgrounds — 40px cells aligned to max-w-7xl container edge */
            .notebook-grid {
                background-image:
                    linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
                background-size: 40px 40px;
                background-position: calc(50% - 620px) 0;
            }

            .notebook-grid-dots {
                background-image: radial-gradient(rgba(255,255,255,0.08) 1px, transparent 1px);
                background-size: 40px 40px;
                background-position: calc(50% - 620px) 0;
            }

            /* Extension lines from card borders — bright near edge, fade out */
            .grid-accent {
                position: relative;
            }
            .grid-accent::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: -120px;
                right: -120px;
                border-top: 1px solid rgba(255,255,255,0.12);
                border-bottom: 1px solid rgba(255,255,255,0.12);
                pointer-events: none;
                -webkit-mask-image: linear-gradient(to right, transparent, white 120px, white calc(100% - 120px), transparent);
                mask-image: linear-gradient(to right, transparent, white 120px, white calc(100% - 120px), transparent);
            }
            .grid-accent::after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                top: -120px;
                bottom: -120px;
                border-left: 1px solid rgba(255,255,255,0.12);
                border-right: 1px solid rgba(255,255,255,0.12);
                pointer-events: none;
                -webkit-mask-image: linear-gradient(to bottom, transparent, white 120px, white calc(100% - 120px), transparent);
                mask-image: linear-gradient(to bottom, transparent, white 120px, white calc(100% - 120px), transparent);
            }

            /* Flowing dash animation for data flow lines */
            @keyframes dashFlow {
                to { stroke-dashoffset: -24; }
            }
            .animate-dash-flow {
                animation: dashFlow 1.5s linear infinite;
            }

            /* Subtle pulse for data nodes */
            @keyframes dataPulse {
                0%, 100% { opacity: 0.7; }
                50% { opacity: 1; }
            }
            .animate-data-pulse {
                animation: dataPulse 3s ease-in-out infinite;
            }

            /* ScrollyHero sticky viewport — prefer svh so iOS Safari's
               dynamic toolbar doesn't resize the sticky container mid-scroll,
               falling back to vh for older browsers that don't support svh. */
            .scrolly-sticky-viewport {
                height: calc(100vh - 64px);
                height: calc(100svh - 64px);
            }

            /* Notebook cell border lines — extend beyond the visualization container */
            .notebook-cell {
                position: relative;
            }
            .notebook-cell::before {
                content: '';
                position: absolute;
                top: -40px;
                bottom: -40px;
                left: 0;
                right: 0;
                border-left: 1px solid rgba(255,255,255,0.12);
                border-right: 1px solid rgba(255,255,255,0.12);
                pointer-events: none;
                z-index: 0;
            }
            .notebook-cell::after {
                content: '';
                position: absolute;
                left: -40px;
                right: -40px;
                top: 0;
                bottom: 0;
                border-top: 1px solid rgba(255,255,255,0.12);
                border-bottom: 1px solid rgba(255,255,255,0.12);
                pointer-events: none;
                z-index: 0;
            }
        `}</style>
    );
};

